import { useEffect, useMemo, useRef, useState } from 'react'

import { courseVideoUrl, useCaptions, useCourse, type Lesson } from '@/api/courses'
import { Badge, Button, Card, CardHeader, Input } from '@/components/ui'

import { saveProgress, type LessonProgress } from './progress'
import { clock, indexAt, parseVtt } from './vtt'

/** How far playback has to move before the position is written down again. */
const SAVE_EVERY_SECONDS = 5

interface TranscriptLine {
  start: number
  text: string
}

/**
 * One lesson: the video with captions, its chapters, and a transcript you can
 * search and click.
 *
 * ## What the browser does and what this does
 *
 * The browser plays the video and the captions (a `<track>` over a blob of the
 * WebVTT the server sent — a blob, so the captions arrive on the same
 * credentialed request the rest of the page uses and not on a second, cross-origin
 * one the track element would make anonymously). This reads the same captions a
 * second time to draw the transcript, and listens to `timeupdate` to keep the
 * current chapter and the current line highlighted.
 *
 * ## Without a video, the lesson is still readable
 *
 * A lesson whose file has not been copied to the server yet is listed, and says
 * so; its narration (the same words the captions carry) is shown as text, so the
 * page is never a dead end while a re-recording is pending.
 */
export function LessonPlayer({
  lesson,
  userKey,
  saved,
  onProgress,
}: {
  lesson: Lesson
  userKey: string
  saved: LessonProgress | undefined
  onProgress: (code: string, progress: LessonProgress) => void
}) {
  const detail = useCourse(lesson.id)
  const captions = useCaptions(lesson.id, lesson.has_video && lesson.has_subtitles)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const lastSaved = useRef(0)

  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(lesson.seconds)
  const [failed, setFailed] = useState(false)
  const [resumeOffered, setResumeOffered] = useState(true)
  const [query, setQuery] = useState('')

  const chapters = detail.data?.chapters ?? lesson.chapters
  const cues = useMemo(() => parseVtt(captions.data ?? ''), [captions.data])

  // The captions as a track. A blob: URL is same-origin, so the track needs no
  // CORS or cookie of its own; it is revoked when the lesson changes.
  const [trackUrl, setTrackUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!captions.data || typeof URL.createObjectURL !== 'function') {
      setTrackUrl(null)
      return undefined
    }
    const url = URL.createObjectURL(new Blob([captions.data], { type: 'text/vtt' }))
    setTrackUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [captions.data])

  // Whatever the position was when the lesson is left, it is remembered.
  const remember = (position: number, total: number) => {
    if (position <= 0) return
    lastSaved.current = position
    onProgress(lesson.id, saveProgress(userKey, lesson.id, position, total))
  }
  const rememberRef = useRef(remember)
  rememberRef.current = remember
  useEffect(() => {
    const video = videoRef.current
    return () => {
      if (video) rememberRef.current(video.currentTime, video.duration || lesson.seconds)
    }
  }, [lesson.id, lesson.seconds])

  function seekTo(seconds: number) {
    const video = videoRef.current
    if (!video) return
    video.currentTime = seconds
    setTime(seconds)
    setResumeOffered(false)
    // Playing needs a gesture, and a click is one; a refusal is not an error.
    void Promise.resolve(video.play?.()).catch(() => undefined)
  }

  // The transcript is the captions where there are any, and the narration of
  // each chapter where there are not.
  const lines: TranscriptLine[] = useMemo(() => {
    if (cues.length > 0) return cues.map((cue) => ({ start: cue.start, text: cue.text }))
    return (detail.data?.chapters ?? [])
      .filter((chapter) => chapter.narration)
      .map((chapter) => ({ start: chapter.start, text: chapter.narration ?? '' }))
  }, [cues, detail.data])
  const needle = query.trim().toLowerCase()
  const shown = needle ? lines.filter((line) => line.text.toLowerCase().includes(needle)) : lines
  const currentLine = indexAt(lines, time)
  const currentChapter = indexAt(chapters, time)

  const resumeAt = saved && !saved.watched && saved.position > 10 ? saved.position : null

  return (
    <div className="flex flex-col gap-4">
      <Card className="gap-3">
        <CardHeader
          title={lesson.title}
          description={lesson.summary}
          actions={
            <div className="flex items-center gap-2">
              {saved?.watched ? <Badge tone="ok">Watched</Badge> : null}
              {lesson.duration ? <Badge tone="neutral">{lesson.duration}</Badge> : null}
            </div>
          }
        />

        {lesson.has_video ? (
          <>
            <video
              key={lesson.id}
              ref={videoRef}
              aria-label={`Video: ${lesson.title}`}
              className="aspect-video w-full rounded-md bg-black"
              controls
              preload="metadata"
              playsInline
              src={courseVideoUrl(lesson.id)}
              onLoadedMetadata={(event) => {
                if (Number.isFinite(event.currentTarget.duration)) {
                  setDuration(event.currentTarget.duration)
                }
              }}
              onTimeUpdate={(event) => {
                const now = event.currentTarget.currentTime
                setTime(now)
                if (Math.abs(now - lastSaved.current) >= SAVE_EVERY_SECONDS) {
                  remember(now, event.currentTarget.duration || duration)
                }
              }}
              onPause={(event) =>
                remember(event.currentTarget.currentTime, event.currentTarget.duration || duration)
              }
              onEnded={(event) =>
                remember(
                  event.currentTarget.duration || duration,
                  event.currentTarget.duration || duration,
                )
              }
              onPlay={() => setResumeOffered(false)}
              onError={() => setFailed(true)}
            >
              {trackUrl ? (
                <track kind="subtitles" srcLang="en" label="English" src={trackUrl} default />
              ) : null}
            </video>

            {failed ? (
              <p role="alert" className="m-0 rounded-md border border-block-line bg-block-bg px-3 py-2 text-meta text-block-fg">
                This video could not be played. Reload the page; if it still fails, tell an
                administrator — the file may be missing or still being copied to the server.
              </p>
            ) : null}

            {resumeAt !== null && resumeOffered ? (
              <div>
                <Button type="button" variant="secondary" onClick={() => seekTo(resumeAt)}>
                  Resume from {clock(resumeAt)}
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <p
            role="note"
            className="m-0 rounded-md border border-divider bg-ground px-3 py-2 text-meta text-muted"
          >
            The video for this lesson has not been added to the server yet. Its narration is
            below, so you can read it in the meantime.
          </p>
        )}

        <dl className="m-0 grid gap-x-4 gap-y-1 text-meta sm:grid-cols-[auto_1fr]">
          {lesson.audience ? (
            <>
              <dt className="text-muted">For</dt>
              <dd className="m-0">{lesson.audience}</dd>
            </>
          ) : null}
        </dl>
      </Card>

      {chapters.length > 0 ? (
        <Card role="group" aria-label="Chapters" className="gap-2">
          <CardHeader title="Chapters" />
          <ol className="m-0 flex list-none flex-col gap-1 p-0">
            {chapters.map((chapter, index) => (
              <li key={`${chapter.start}-${index}`}>
                <button
                  type="button"
                  disabled={!lesson.has_video}
                  aria-current={index === currentChapter ? 'true' : undefined}
                  onClick={() => seekTo(chapter.start)}
                  className={
                    index === currentChapter
                      ? 'flex w-full items-baseline gap-3 rounded-md border border-brand-700 bg-brand-50 px-3 py-1.5 text-left text-body'
                      : 'flex w-full items-baseline gap-3 rounded-md border border-divider px-3 py-1.5 text-left text-body hover:bg-ground disabled:opacity-60'
                  }
                >
                  <span className="w-12 shrink-0 font-mono text-meta text-muted">
                    {chapter.start_label || clock(chapter.start)}
                  </span>
                  <span className="font-medium">{chapter.title}</span>
                  {chapter.kind ? (
                    <span className="ml-auto text-micro text-muted">{chapter.kind}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ol>
        </Card>
      ) : null}

      {lines.length > 0 ? (
        <Card role="group" aria-label="Transcript" className="gap-2">
          <CardHeader
            title="Transcript"
            description={
              lesson.has_video
                ? 'Click a line to jump to it.'
                : 'The words spoken in the lesson, chapter by chapter.'
            }
          />
          <Input
            type="search"
            aria-label="Search the transcript"
            placeholder="Search the transcript"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {shown.length === 0 ? (
            <p className="m-0 text-meta text-muted">Nothing in the transcript matches “{query}”.</p>
          ) : (
            <ol className="m-0 flex max-h-[420px] list-none flex-col gap-0.5 overflow-auto p-0">
              {shown.map((line) => (
                <li key={`${line.start}-${line.text.slice(0, 24)}`}>
                  <button
                    type="button"
                    disabled={!lesson.has_video}
                    aria-current={lines.indexOf(line) === currentLine ? 'true' : undefined}
                    onClick={() => seekTo(line.start)}
                    className={
                      lines.indexOf(line) === currentLine
                        ? 'flex w-full gap-3 rounded-md bg-brand-100 px-2 py-1 text-left text-body font-medium'
                        : 'flex w-full gap-3 rounded-md px-2 py-1 text-left text-body hover:bg-ground disabled:hover:bg-transparent'
                    }
                  >
                    <span className="w-12 shrink-0 font-mono text-meta text-muted">
                      {clock(line.start)}
                    </span>
                    <span>{line.text}</span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </Card>
      ) : null}
    </div>
  )
}
